/* Bhagwati Arts theme scripts.
   Plain JavaScript, no libraries. Most behaviour is attached through data attributes,
   and clicks are handled on the document so content swapped in later keeps working. */
(function () {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const strings = theme.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function rootPath(path) {
    return (theme.routes.root || '/').replace(/\/$/, '') + path;
  }

  /* Toast ------------------------------------------------------------------ */

  let toastTimer;
  function toast(message) {
    const el = document.getElementById('Toast');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.hidden = true;
    }, 2800);
  }

  /* Drawers ---------------------------------------------------------------- */

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  const Drawer = {
    open(id, trigger) {
      const el = document.getElementById(id);
      if (!el) return false;
      el.hidden = false;
      el.trigger = trigger || document.activeElement;
      requestAnimationFrame(() => el.classList.add('is-open'));
      document.body.classList.add('is-locked');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      const first = $('[data-drawer-close]:not(.drawer__overlay)', el);
      if (first) setTimeout(() => first.focus(), 60);
      return true;
    },
    close(el) {
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      if (el.trigger && el.trigger.setAttribute) {
        el.trigger.setAttribute('aria-expanded', 'false');
        if (el.trigger.focus) el.trigger.focus();
      }
      setTimeout(
        () => {
          if (!el.classList.contains('is-open')) el.hidden = true;
        },
        reducedMotion ? 0 : 300
      );
    },
    closeAll() {
      $$('.drawer.is-open').forEach((el) => Drawer.close(el));
    },
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      Drawer.closeAll();
      closeLightbox();
      closeFilters();
      hidePredictive();
      return;
    }
    if (lightbox && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      lightbox.step(event.key === 'ArrowLeft' ? -1 : 1);
      return;
    }
    if (event.key !== 'Tab') return;
    const open = $('.drawer.is-open');
    if (!open) return;
    const items = $$(FOCUSABLE, open).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Header ----------------------------------------------------------------- */

  function initHeader() {
    const header = $('.section-header');
    if (!header) return;
    const topbar = $('[data-topbar]', header);
    const measure = () => {
      const topbarHeight = topbar ? topbar.offsetHeight : 0;
      header.style.setProperty('--topbar-h', topbarHeight + 'px');
      // Sticky panels further down the page (buy card, filters, cart summary) sit below this.
      document.documentElement.style.setProperty('--sticky-top', header.offsetHeight - topbarHeight + 'px');
      document.dispatchEvent(new CustomEvent('theme:header-measured'));
    };
    measure();
    window.addEventListener('resize', debounce(measure, 150));
    if ('ResizeObserver' in window) new ResizeObserver(debounce(measure, 100)).observe(header);

    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const scrolled = window.scrollY > 140;
          if (header.classList.contains('is-scrolled') !== scrolled) {
            header.classList.toggle('is-scrolled', scrolled);
            if (!scrolled) {
              const search = $('[data-search]', header);
              if (search) search.classList.remove('is-open');
            }
          }
          ticking = false;
        });
      },
      { passive: true }
    );
  }

  /* Hero slideshow --------------------------------------------------------- */

  function initSlideshow(el) {
    if (el.dataset.ready) return;
    const slides = $$('[data-slide]', el);
    if (!slides.length) return;
    const copies = $$('[data-slide-copy]', el);
    const current = $('[data-slideshow-current]', el);
    const progress = $('[data-slideshow-progress]', el);
    const seconds = parseInt(el.dataset.interval, 10) || 6;
    const canPlay = el.dataset.autoplay === 'true' && slides.length > 1 && !reducedMotion;
    let index = 0;
    let timer = null;

    el.style.setProperty('--hero-interval', seconds + 's');

    function show(n) {
      index = (n + slides.length) % slides.length;
      slides.forEach((slide, k) => {
        slide.classList.toggle('is-active', k === index);
        slide.inert = k !== index;
      });
      copies.forEach((copy, k) => {
        copy.classList.toggle('is-active', k === index);
        copy.inert = k !== index;
      });
      if (current) current.textContent = String(index + 1).padStart(2, '0');
    }

    // The thin line fills up while a slide is on screen.
    function runProgress(on) {
      if (!progress) return;
      progress.classList.remove('is-running');
      if (on) {
        void progress.offsetWidth;
        progress.classList.add('is-running');
      }
    }

    function stop() {
      clearInterval(timer);
      timer = null;
      runProgress(false);
    }

    function start() {
      if (!canPlay) return;
      stop();
      runProgress(true);
      timer = setInterval(() => {
        show(index + 1);
        runProgress(true);
      }, seconds * 1000);
    }

    const go = (n) => {
      show(n);
      start();
    };

    const prev = $('[data-slideshow-prev]', el);
    const next = $('[data-slideshow-next]', el);
    if (prev) prev.addEventListener('click', () => go(index - 1));
    if (next) next.addEventListener('click', () => go(index + 1));

    const stage = $('.hero__stage', el) || el;
    stage.addEventListener('mouseenter', stop);
    stage.addEventListener('mouseleave', start);
    stage.addEventListener('focusin', stop);
    stage.addEventListener('focusout', start);

    let touchX = null;
    stage.addEventListener('touchstart', (event) => { touchX = event.touches[0].clientX; }, { passive: true });
    stage.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const dx = event.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) < 44) return;
      go(index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    // Theme editor: show the slide being edited
    el.addEventListener('shopify:block:select', (event) => {
      const n = copies.indexOf(event.target);
      if (n > -1) {
        show(n);
        stop();
      }
    });

    show(0);
    el.dataset.ready = 'true';
    start();
  }

  /* Product and review rows ------------------------------------------------ */

  function initSlider(el) {
    if (el.dataset.sliderReady) return;
    const track = $('[data-slider-track]', el);
    const prev = $('[data-slider-prev]', el);
    const next = $('[data-slider-next]', el);
    if (!track || !prev || !next) return;

    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * 0.9, behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * 0.9, behavior: 'smooth' }));
    track.addEventListener('scroll', debounce(update, 80), { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    el.updateSlider = update;
    el.dataset.sliderReady = 'true';
    update();
  }

  /* Scrolling messages ----------------------------------------------------- */

  function initMarquee(el) {
    if (el.dataset.ready || reducedMotion) return;
    const track = $('.marquee__track', el);
    const group = $('.marquee__group', el);
    if (!track || !group || !group.offsetWidth) return;
    const perHalf = Math.max(1, Math.ceil(window.innerWidth / group.offsetWidth));
    const clone = (hidden) => {
      const copy = group.cloneNode(true);
      if (hidden) {
        copy.setAttribute('aria-hidden', 'true');
        $$('a', copy).forEach((a) => a.setAttribute('tabindex', '-1'));
      }
      return copy;
    };
    track.innerHTML = '';
    for (let i = 0; i < perHalf * 2; i += 1) track.appendChild(clone(i > 0));
    track.style.setProperty('--marquee-time', Math.round((group.offsetWidth * perHalf) / 55) + 's');
    el.dataset.ready = 'true';
  }

  /* Room tabs -------------------------------------------------------------- */

  async function openTab(tab) {
    const section = tab.closest('[data-tabs]');
    const panel = section && $('[data-tab-panel]', section);
    if (!panel || tab.classList.contains('is-active')) return;
    $$('[data-tab]', section).forEach((other) => {
      other.classList.toggle('is-active', other === tab);
      other.setAttribute('aria-selected', other === tab ? 'true' : 'false');
    });
    panel.classList.add('is-loading');
    try {
      const response = await fetch(tab.dataset.tabUrl + '?section_id=ajax-product-row');
      if (!response.ok) throw new Error('tab');
      const holder = $('.shopify-section', parseHTML(await response.text()));
      if (holder) {
        panel.innerHTML = holder.innerHTML;
        panel.scrollLeft = 0;
        Wishlist.sync(panel);
        const slider = panel.closest('[data-slider]');
        if (slider && slider.updateSlider) slider.updateSlider();
      }
    } catch (error) {
      window.location.href = tab.href;
    } finally {
      panel.classList.remove('is-loading');
    }
  }

  /* Cart ------------------------------------------------------------------- */

  const Cart = {
    sectionIds() {
      return $$('[data-cart-root]').map((root) => root.dataset.sectionId).filter(Boolean);
    },

    applySections(sections) {
      if (!sections) return false;
      let count = null;
      $$('[data-cart-root]').forEach((root) => {
        const html = sections[root.dataset.sectionId];
        if (!html) return;
        const fresh = $('[data-cart-root]', parseHTML(html));
        if (!fresh) return;
        root.innerHTML = fresh.innerHTML;
        count = fresh.dataset.cartCountValue;
        root.dataset.cartCountValue = count;
      });
      if (count !== null) Cart.setCount(count);
      return count !== null;
    },

    setCount(count) {
      const n = parseInt(count, 10) || 0;
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = n;
        el.hidden = n === 0;
      });
    },

    async refresh() {
      const ids = Cart.sectionIds();
      if (!ids.length) {
        const cart = await (await fetch(rootPath('/cart.js'), { headers: { Accept: 'application/json' } })).json();
        Cart.setCount(cart.item_count);
        return;
      }
      const response = await fetch(window.location.pathname + '?sections=' + ids.join(','));
      Cart.applySections(await response.json());
    },

    async add(form, button) {
      const errorBox = $('[data-form-error]', form);
      if (errorBox) errorBox.hidden = true;
      if (button) button.classList.add('is-loading');
      try {
        const body = new FormData(form);
        const ids = Cart.sectionIds();
        if (ids.length) {
          body.append('sections', ids.join(','));
          body.append('sections_url', window.location.pathname);
        }
        const response = await fetch(theme.routes.cartAdd + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || strings.cartError);
        }
        if (theme.cartType === 'page' || !document.getElementById('CartDrawer')) {
          if (theme.template === 'cart') {
            await Cart.refresh();
            toast(strings.added);
          } else {
            window.location.href = theme.routes.cart;
          }
          return;
        }
        if (!Cart.applySections(data.sections)) await Cart.refresh();
        Drawer.open('CartDrawer', button);
      } catch (error) {
        const message = error && error.message ? error.message : strings.cartError;
        if (errorBox) {
          errorBox.textContent = message;
          errorBox.hidden = false;
        } else {
          toast(message);
        }
      } finally {
        if (button) button.classList.remove('is-loading');
      }
    },

    async change(line, quantity) {
      const lists = $$('.citems');
      lists.forEach((list) => list.classList.add('is-loading'));
      try {
        const response = await fetch(theme.routes.cartChange + '.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity),
            sections: Cart.sectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          toast(data.description || data.message || strings.cartError);
          await Cart.refresh();
          return;
        }
        if (!Cart.applySections(data.sections)) await Cart.refresh();
      } catch (error) {
        toast(strings.cartError);
      } finally {
        $$('.citems').forEach((list) => list.classList.remove('is-loading'));
      }
    },

    saveNote: debounce((note) => {
      fetch(theme.routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      }).catch(() => {});
    }, 500),
  };

  /* Product page ----------------------------------------------------------- */

  function productVariants(product) {
    if (!product.variantData) {
      const node = $('[data-variants-json]', product);
      try {
        product.variantData = node ? JSON.parse(node.textContent) : [];
      } catch (error) {
        product.variantData = [];
      }
    }
    return product.variantData;
  }

  function selectedOptions(product) {
    return $$('.variant-option', product).map((fieldset) => {
      const checked = $('input:checked', fieldset);
      return checked ? checked.value : null;
    });
  }

  function markUnavailable(product) {
    const variants = productVariants(product);
    const selected = selectedOptions(product);
    $$('.variant-option', product).forEach((fieldset, position) => {
      $$('[data-option-value]', fieldset).forEach((input) => {
        const wanted = selected.slice();
        wanted[position] = input.value;
        const match = variants.find((variant) => variant.options.every((option, i) => option === wanted[i]));
        input.closest('.pill').classList.toggle('is-unavailable', !match || !match.available);
      });
    });
  }

  function setBuyState(product, available, label) {
    $$('[data-add-button]', product).forEach((button) => {
      button.disabled = !available;
      const text = $('[data-add-label]', button);
      if (text) text.textContent = label;
    });
  }

  async function onVariantChange(input) {
    const product = input.closest('[data-product]');
    if (!product) return;
    const fieldset = input.closest('.variant-option');
    const shown = $('[data-option-selected]', fieldset);
    if (shown) shown.textContent = input.value;

    const variants = productVariants(product);
    const selected = selectedOptions(product);
    const variant = variants.find((item) => item.options.every((option, i) => option === selected[i]));
    markUnavailable(product);

    if (!variant) {
      setBuyState(product, false, strings.unavailable);
      return;
    }

    const idInput = $('[data-variant-id]', product);
    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    setBuyState(product, variant.available, variant.available ? strings.addToCart : strings.soldOut);

    const url = product.dataset.url + '?variant=' + variant.id;
    window.history.replaceState({}, '', url);

    if (variant.featured_media) {
      const gallery = $('[data-gallery]', product);
      if (gallery && gallery.showMedia) gallery.showMedia(String(variant.featured_media.id));
    }

    try {
      const response = await fetch(url + '&section_id=' + encodeURIComponent(product.dataset.sectionId));
      if (!response.ok) return;
      const fresh = parseHTML(await response.text());
      $$('[data-swap]', product).forEach((target) => {
        const source = $('[data-swap="' + target.dataset.swap + '"]', fresh);
        if (source) target.innerHTML = source.innerHTML;
      });
    } catch (error) {
      /* the prices already on the page stay as they are */
    }
  }

  function initGallery(gallery) {
    if (gallery.dataset.ready) return;
    const track = $('[data-gallery-track]', gallery);
    if (!track) return;
    const counter = $('[data-gallery-current]', gallery);
    const isCarousel = () => track.scrollWidth > track.clientWidth + 4;

    // Phones: keep the "2 / 6" counter in step with the swipe.
    const sync = () => {
      if (!counter || !isCarousel()) return;
      const total = $$('[data-media-id]', track).length;
      const current = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)) + 1;
      counter.textContent = String(Math.min(current, total));
      $$('video', track).forEach((video) => video.pause());
    };
    track.addEventListener('scroll', debounce(sync, 60), { passive: true });

    // Choosing an option brings that variant's photo to the front of the mosaic.
    gallery.showMedia = (mediaId) => {
      const item = $$('[data-media-id]', track).find((node) => node.dataset.mediaId === mediaId);
      if (!item) return;
      if (item !== track.firstElementChild) track.prepend(item);
      if (isCarousel()) {
        track.scrollTo({ left: 0, behavior: 'auto' });
        if (counter) counter.textContent = '1';
        return;
      }
      const box = item.getBoundingClientRect();
      if (box.top < 0 || box.top > window.innerHeight * 0.6) {
        const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-top')) || 0;
        window.scrollTo({
          top: window.scrollY + box.top - offset - 16,
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      }
    };
    gallery.dataset.ready = 'true';
  }

  /* Full-screen photos, with arrows to step through all of them */

  let lightbox = null;

  function openLightbox(photos, startIndex) {
    closeLightbox();
    if (!photos.length) return;
    let index = Math.max(0, Math.min(startIndex, photos.length - 1));
    const opener = document.activeElement;

    lightbox = document.createElement('div');
    lightbox.className = 'lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.opener = opener;

    const image = document.createElement('img');
    const count = document.createElement('p');
    count.className = 'lightbox__count';

    const iconButton = (className, label, path) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'icon-btn ' + className;
      button.setAttribute('aria-label', label);
      button.innerHTML =
        '<svg class="icon" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' +
        path +
        '"/></svg>';
      return button;
    };
    const close = iconButton('lightbox__close', strings.close || 'Close', 'm6 6 12 12M18 6 6 18');
    const prev = iconButton('lightbox__nav lightbox__nav--prev', strings.previous || 'Previous', 'm15 6-6 6 6 6');
    const next = iconButton('lightbox__nav lightbox__nav--next', strings.next || 'Next', 'm9 6 6 6-6 6');

    const show = (n) => {
      index = (n + photos.length) % photos.length;
      const photo = photos[index];
      image.src = photo.preview || photo.src;
      image.alt = photo.alt || '';
      count.textContent = index + 1 + ' / ' + photos.length;
      if (photo.preview && photo.preview !== photo.src) {
        const large = new Image();
        large.onload = () => {
          if (lightbox && photos[index] === photo) image.src = photo.src;
        };
        large.src = photo.src;
      }
    };
    lightbox.step = (direction) => show(index + direction);

    prev.addEventListener('click', (event) => { event.stopPropagation(); show(index - 1); });
    next.addEventListener('click', (event) => { event.stopPropagation(); show(index + 1); });
    image.addEventListener('click', (event) => event.stopPropagation());
    lightbox.addEventListener('click', closeLightbox);

    let touchX = null;
    lightbox.addEventListener('touchstart', (event) => { touchX = event.touches[0].clientX; }, { passive: true });
    lightbox.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const dx = event.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) > 44) show(index + (dx < 0 ? 1 : -1));
    }, { passive: true });

    lightbox.append(image, close);
    if (photos.length > 1) lightbox.append(prev, next, count);
    document.body.appendChild(lightbox);
    document.body.classList.add('is-locked');
    show(index);
    close.focus();
  }

  function closeLightbox() {
    if (!lightbox) return;
    const opener = lightbox.opener;
    lightbox.remove();
    lightbox = null;
    if (!$('.drawer.is-open')) document.body.classList.remove('is-locked');
    if (opener && opener.focus) opener.focus();
  }

  function galleryPhotos(gallery) {
    return $$('[data-zoom]', gallery).map((button) => {
      const image = $('img', button);
      return {
        src: button.dataset.zoom,
        preview: image ? image.currentSrc || image.src : '',
        alt: image ? image.alt : '',
        button,
      };
    });
  }

  /* The photo column and the buy column both follow the page. A column taller than the
     screen sticks by its bottom edge instead of its top, so nothing in it is cut off. */

  function initBuyCard(product) {
    const columns = $$('.product__gallery, .product__side', product);
    if (!columns.length) return;
    const place = () => {
      const offset = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-top')) || 0) + 16;
      columns.forEach((column) => {
        if (getComputedStyle(column).position !== 'sticky') {
          column.style.top = '';
          return;
        }
        column.style.top = Math.min(offset, window.innerHeight - column.offsetHeight - 16) + 'px';
      });
    };
    place();
    window.addEventListener('resize', debounce(place, 150));
    document.addEventListener('theme:header-measured', place);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(debounce(place, 80));
      columns.forEach((column) => observer.observe(column));
    }
  }

  function initStickyBuy(product) {
    const bar = $('[data-sticky-buy]', product);
    const target = $('.product-form__add', product);
    if (!bar || !target) return;
    // Checked on scroll rather than with an IntersectionObserver: a fast flick can carry the
    // button from below the screen to above it without it ever being "seen" in between.
    let ticking = false;
    const update = () => {
      ticking = false;
      const show = target.getBoundingClientRect().bottom < 0;
      if (bar.hidden !== show) return;
      bar.hidden = !show;
      document.body.classList.toggle('has-sticky-buy', show);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  async function initRecommendations(el) {
    if (el.dataset.loaded || !el.dataset.url) return;
    el.dataset.loaded = 'true';
    try {
      const response = await fetch(el.dataset.url);
      if (!response.ok) return;
      const fresh = $('[data-recommendations]', parseHTML(await response.text()));
      if (fresh && fresh.innerHTML.trim()) {
        el.innerHTML = fresh.innerHTML;
        initAll(el);
      }
    } catch (error) {
      /* the section simply stays empty */
    }
  }

  /* Cards fetched one by one (wishlist, recently viewed) -------------------- */

  async function fetchCard(handle) {
    try {
      const response = await fetch(rootPath('/products/' + encodeURIComponent(handle)) + '?section_id=card-product');
      if (!response.ok) return null;
      const card = $('.pcard', parseHTML(await response.text()));
      return card ? card.outerHTML : null;
    } catch (error) {
      return null;
    }
  }

  function readList(key) {
    try {
      const value = JSON.parse(window.localStorage.getItem(key));
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  }

  function writeList(key, list) {
    try {
      window.localStorage.setItem(key, JSON.stringify(list));
    } catch (error) {
      /* private browsing: the list just will not be remembered */
    }
  }

  const Wishlist = {
    key: 'bhagwati:wishlist',
    get() {
      return readList(Wishlist.key);
    },
    toggle(handle) {
      const list = Wishlist.get();
      const at = list.indexOf(handle);
      if (at > -1) {
        list.splice(at, 1);
        toast(strings.wishlistRemoved);
      } else {
        list.unshift(handle);
        toast(strings.wishlistAdded);
      }
      writeList(Wishlist.key, list.slice(0, 60));
      Wishlist.sync(document);
      Wishlist.render();
    },
    sync(root) {
      const list = Wishlist.get();
      $$('[data-wishlist]', root).forEach((button) => {
        const saved = list.includes(button.dataset.wishlist);
        button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        button.setAttribute('aria-label', saved ? strings.wishlistRemove : strings.wishlistAdd);
      });
      $$('[data-wishlist-count]').forEach((el) => {
        el.textContent = list.length;
        el.hidden = list.length === 0;
      });
    },
    async render() {
      const grids = $$('[data-wishlist-grid]').filter((grid) => grid.offsetParent !== null || grid.closest('.page'));
      if (!grids.length) return;
      const list = Wishlist.get();
      const cards = await Promise.all(list.map(fetchCard));
      const html = cards.filter(Boolean).map((card) => '<div>' + card + '</div>').join('');
      grids.forEach((grid) => {
        grid.innerHTML = html;
        const empty = grid.parentElement.querySelector('[data-wishlist-empty]');
        if (empty) empty.hidden = html !== '';
        Wishlist.sync(grid);
      });
    },
  };

  const Recent = {
    key: 'bhagwati:recent',
    track(handle) {
      const list = readList(Recent.key).filter((item) => item !== handle);
      list.unshift(handle);
      writeList(Recent.key, list.slice(0, 14));
    },
    async render(section) {
      if (section.dataset.loaded) return;
      section.dataset.loaded = 'true';
      const limit = parseInt(section.dataset.limit, 10) || 8;
      const handles = readList(Recent.key)
        .filter((handle) => handle !== section.dataset.current)
        .slice(0, limit);
      if (handles.length < 2) return;
      const cards = (await Promise.all(handles.map(fetchCard))).filter(Boolean);
      if (cards.length < 2) return;
      const track = $('[data-recently-viewed-track]', section);
      track.innerHTML = cards.map((card) => '<div class="slider__slide">' + card + '</div>').join('');
      section.hidden = false;
      Wishlist.sync(section);
      initAll(section);
    },
  };

  /* Filters ---------------------------------------------------------------- */

  function facetContainer() {
    return $('[data-facets-container]');
  }

  function closeFilters() {
    const container = facetContainer();
    if (!container || !container.classList.contains('filters-open')) return;
    container.classList.remove('filters-open');
    document.body.classList.remove('is-locked');
  }

  async function loadFacets(url, push) {
    const container = facetContainer();
    if (!container) {
      window.location.href = url;
      return;
    }
    const openGroups = $$('details.facet', container)
      .filter((group) => group.open)
      .map((group) => group.dataset.facet);
    container.classList.add('is-loading');
    try {
      const joiner = url.includes('?') ? '&' : '?';
      const response = await fetch(url + joiner + 'section_id=' + encodeURIComponent(container.dataset.sectionId));
      if (!response.ok) throw new Error('facets');
      const fresh = $('[data-facets-container]', parseHTML(await response.text()));
      if (!fresh) throw new Error('facets');
      container.innerHTML = fresh.innerHTML;
      container.classList.toggle('collection-layout--nofilters', fresh.classList.contains('collection-layout--nofilters'));
      $$('details.facet', container).forEach((group) => {
        if (openGroups.includes(group.dataset.facet)) group.open = true;
      });
      if (push) window.history.pushState({ facets: true }, '', url);
      Wishlist.sync(container);
      if (!container.classList.contains('filters-open')) {
        const top = container.getBoundingClientRect().top + window.scrollY - 150;
        if (window.scrollY > top) window.scrollTo({ top, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
    } catch (error) {
      window.location.href = url;
    } finally {
      container.classList.remove('is-loading');
    }
  }

  function facetUrl(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value !== '') params.append(key, value);
    });
    const query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  }

  const submitFacets = debounce((form) => loadFacets(facetUrl(form), true), 350);

  window.addEventListener('popstate', () => {
    if (facetContainer()) loadFacets(window.location.pathname + window.location.search, false);
  });

  /* Search suggestions ----------------------------------------------------- */

  let predictiveRequest = null;

  function hidePredictive() {
    $$('[data-predictive-results]').forEach((box) => {
      box.hidden = true;
    });
    $$('[data-predictive-input]').forEach((input) => input.setAttribute('aria-expanded', 'false'));
  }

  const runPredictive = debounce(async (input) => {
    const box = $('[data-predictive-results]', input.form);
    if (!box) return;
    const terms = input.value.trim();
    if (terms.length < 2) {
      hidePredictive();
      return;
    }
    if (predictiveRequest) predictiveRequest.abort();
    predictiveRequest = new AbortController();
    try {
      const url =
        theme.routes.predictiveSearch +
        '?q=' + encodeURIComponent(terms) +
        '&resources[type]=product,collection,query&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search';
      const response = await fetch(url, { signal: predictiveRequest.signal });
      if (!response.ok) return;
      const inner = $('[data-predictive-inner]', parseHTML(await response.text()));
      if (!inner || input.value.trim() !== terms) return;
      box.innerHTML = inner.outerHTML;
      box.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    } catch (error) {
      /* aborted or offline: the normal search form still works */
    }
  }, 260);

  /* Small helpers ---------------------------------------------------------- */

  function initCollapse(root) {
    $$('[data-collapse]', root).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = 'true';
      const toggle = el.nextElementSibling;
      if (!toggle || !toggle.matches('[data-collapse-toggle]')) return;
      const limit = parseFloat(getComputedStyle(el).fontSize) * 7.2 * 1.25;
      if (el.scrollHeight <= limit) return;
      el.classList.add('is-clamped');
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        const clamped = el.classList.toggle('is-clamped');
        toggle.textContent = clamped ? toggle.dataset.more : toggle.dataset.less;
      });
    });
  }

  function initReels(root) {
    const reels = $$('[data-reel]', root).filter((reel) => !reel.dataset.ready);
    if (!reels.length) return;
    const observer =
      'IntersectionObserver' in window && !reducedMotion
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const video = $('video', entry.target);
                if (!video) return;
                if (entry.isIntersecting && !entry.target.dataset.paused) {
                  video.muted = true;
                  video.play().then(() => entry.target.classList.add('is-playing')).catch(() => {});
                } else {
                  video.pause();
                  entry.target.classList.remove('is-playing');
                }
              });
            },
            { threshold: 0.6 }
          )
        : null;
    reels.forEach((reel) => {
      reel.dataset.ready = 'true';
      if (observer) observer.observe(reel);
    });
  }

  function initAddressForms(root) {
    $$('[data-country-select]', root).forEach((country) => {
      if (country.dataset.ready) return;
      country.dataset.ready = 'true';
      const form = country.closest('form');
      const province = $('[data-province-select]', form);
      const field = $('[data-province-field]', form);
      if (country.dataset.default) country.value = country.dataset.default;

      const fill = () => {
        const option = country.options[country.selectedIndex];
        let provinces = [];
        try {
          provinces = JSON.parse((option && option.dataset.provinces) || '[]');
        } catch (error) {
          provinces = [];
        }
        if (!province || !field) return;
        province.innerHTML = '';
        provinces.forEach(([value, label]) => {
          const item = document.createElement('option');
          item.value = value;
          item.textContent = label;
          province.appendChild(item);
        });
        field.hidden = provinces.length === 0;
        if (province.dataset.default) province.value = province.dataset.default;
      };
      country.addEventListener('change', fill);
      fill();
    });
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise((resolve) => {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
      } catch (error) {
        /* nothing more to try */
      }
      area.remove();
      resolve();
    });
  }

  /* One click handler for the whole page ----------------------------------- */

  document.addEventListener('click', (event) => {
    const target = event.target;

    const opener = target.closest('[data-drawer-open]');
    if (opener) {
      const id = opener.dataset.drawerOpen;
      if (Drawer.open(id, opener)) {
        event.preventDefault();
        if (id === 'WishlistDrawer') Wishlist.render();
      }
      return;
    }

    const closer = target.closest('[data-drawer-close]');
    if (closer) {
      Drawer.close(closer.closest('.drawer'));
      return;
    }

    const wish = target.closest('[data-wishlist]');
    if (wish) {
      event.preventDefault();
      Wishlist.toggle(wish.dataset.wishlist);
      return;
    }

    const tab = target.closest('[data-tab]');
    if (tab) {
      event.preventDefault();
      openTab(tab);
      return;
    }

    const plus = target.closest('[data-qty-plus]');
    const minus = target.closest('[data-qty-minus]');
    if (plus || minus) {
      const input = $('.qty__input', (plus || minus).closest('[data-qty]'));
      if (!input) return;
      const min = input.min === '' ? 0 : Number(input.min);
      const next = Math.max(min, (Number(input.value) || 0) + (plus ? 1 : -1));
      if (next === Number(input.value)) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const remove = target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      Cart.change(remove.dataset.lineRemove, 0);
      return;
    }

    const zoom = target.closest('[data-zoom]');
    if (zoom) {
      const photos = galleryPhotos(zoom.closest('[data-gallery]') || document);
      openLightbox(photos, Math.max(0, photos.findIndex((photo) => photo.button === zoom)));
      return;
    }

    const zoomAll = target.closest('[data-zoom-all]');
    if (zoomAll) {
      openLightbox(galleryPhotos(zoomAll.closest('[data-gallery]') || document), 6);
      return;
    }

    const copy = target.closest('[data-copy]');
    if (copy) {
      copyText(copy.dataset.copy).then(() => toast(strings.copied));
      return;
    }

    const share = target.closest('[data-share]');
    if (share) {
      const data = { title: share.dataset.shareTitle, url: share.dataset.shareUrl };
      if (navigator.share) {
        navigator.share(data).catch(() => {});
      } else {
        copyText(data.url).then(() => toast(strings.copied));
      }
      return;
    }

    const searchToggle = target.closest('[data-search-toggle]');
    if (searchToggle) {
      const search = $('[data-search]');
      if (search) {
        const open = search.classList.toggle('is-open');
        if (open) $('input[type="search"]', search).focus();
      }
      return;
    }

    if (target.closest('[data-filter-open]')) {
      const container = facetContainer();
      if (container) {
        container.classList.add('filters-open');
        document.body.classList.add('is-locked');
      }
      return;
    }

    if (target.closest('[data-filter-close]')) {
      closeFilters();
      return;
    }

    const facetLink = target.closest('[data-facet-link]');
    if (facetLink && facetContainer()) {
      event.preventDefault();
      loadFacets(facetLink.getAttribute('href'), true);
      return;
    }

    const reelToggle = target.closest('[data-reel-toggle]');
    if (reelToggle) {
      const reel = reelToggle.closest('[data-reel]');
      const video = $('video', reel);
      if (!video) return;
      if (video.paused) {
        delete reel.dataset.paused;
        video.play().then(() => reel.classList.add('is-playing')).catch(() => {});
      } else {
        reel.dataset.paused = 'true';
        video.pause();
        reel.classList.remove('is-playing');
      }
      return;
    }

    if (!target.closest('[data-search]')) hidePredictive();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (form.matches('[data-product-form]')) {
      event.preventDefault();
      Cart.add(form, event.submitter || $('[type="submit"]', form));
      return;
    }

    if (form.matches('[data-facets]')) {
      event.preventDefault();
      loadFacets(facetUrl(form), true);
      return;
    }

    if (form.dataset.confirm && !window.confirm(form.dataset.confirm)) {
      event.preventDefault();
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (target.matches('[data-line-input]')) {
      Cart.change(target.dataset.lineInput, Math.max(0, parseInt(target.value, 10) || 0));
      return;
    }

    if (target.matches('[data-option-value]')) {
      onVariantChange(target);
      return;
    }

    if (target.matches('[data-cart-note]')) {
      Cart.saveNote(target.value);
      return;
    }

    const facetForm = target.form && target.form.matches('[data-facets]') ? target.form : null;
    if (facetForm) submitFacets(facetForm);
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-predictive-input]')) runPredictive(event.target);
  });

  document.addEventListener('focusin', (event) => {
    if (event.target.matches('[data-predictive-input]') && event.target.value.trim().length > 1) {
      runPredictive(event.target);
    }
  });

  /* Start ------------------------------------------------------------------ */

  function initAll(root) {
    $$('[data-slideshow]', root).forEach(initSlideshow);
    $$('[data-slider]', root).forEach(initSlider);
    $$('[data-marquee]', root).forEach(initMarquee);
    $$('[data-gallery]', root).forEach(initGallery);
    $$('[data-recommendations]', root).forEach(initRecommendations);
    $$('[data-recently-viewed]', root).forEach((section) => Recent.render(section));
    initCollapse(root);
    initReels(root);
    initAddressForms(root);
    Wishlist.sync(root);
  }

  function start() {
    initHeader();
    initAll(document);

    const product = $('[data-product]');
    if (product) {
      markUnavailable(product);
      initStickyBuy(product);
      initBuyCard(product);
      if (product.dataset.handle) Recent.track(product.dataset.handle);
      const variantId = new URLSearchParams(window.location.search).get('variant');
      const variant = variantId && productVariants(product).find((item) => String(item.id) === variantId);
      if (variant && variant.featured_media) {
        const gallery = $('[data-gallery]', product);
        if (gallery && gallery.showMedia) gallery.showMedia(String(variant.featured_media.id));
      }
    }

    if ($('.page [data-wishlist-grid]')) Wishlist.render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: sections are re-rendered without a page load
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
